// MultiCharacterStage — manages additional CharacterView instances on the
// HomeScreen's existing canvas/scene/renderer when the local player is in
// a party. The local player is rendered by HomeScreen itself; this stage
// adds/removes party-mates around them.
//
// World positions (party-mate dropping in):
//   joinIndex 1 → x = +1.2 (right of local)
//   joinIndex 2 → x = -1.2 (left of local)
//   joinIndex 3 → x = +2.4 (far right)
//
// Spacing is tuned so all 4 characters fit within the 1300×1080 canvas
// at the local player's existing camera (FOV 28°, distance 8.4) — that
// covers ±2.52 world units horizontally, comfortably containing the
// extreme +2.4 / -1.2 endpoints.
//
// Each remote character runs the same head-tracking + beat-locked nod the
// local character runs, but anchored to its own on-screen head position.
// On the user's screen, every character looks at the user's local mouse
// (cute hive-mind effect — confirmed in plan).
//
// DOM head badges (name pin) are projected from the head's world position
// each frame and positioned via CSS transform on absolute-positioned divs
// over the canvas.

import * as THREE from 'three';
import { CharacterView } from './CharacterView';
import { Bone, BONE_COUNT, quatFromAxisAngle, quatMul } from '../sim/Ragdoll';
import { emptySnapshot, type CharacterSnapshot } from '../sim/SimState';
import { paletteFromBase, badgeColorsFromBase } from '../ui/palette';
import { renderHeadLabelSvg } from '../ui/HomeScreen';
import type { HatId } from './HatModels';

export interface PartyMemberView {
  sessionId: string;
  name: string;
  color: string;
  hatId: HatId | null;
  level: number;
  joinIndex: number; // 0..3
  isLocal: boolean;
  isOwner: boolean;
}

export interface StageHandle {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  /** Wrapping div over the canvas — head badge DOM elements are appended
   *  here so they overlay the 3D scene at the projected head position. */
  badgeRoot: HTMLElement;
  /** Canvas client size in CSS px (NOT renderer pixel size). Used to
   *  project world coordinates → screen positions for badges. */
  getCanvasSize(): { width: number; height: number };
  /** The HomeScreen's already-loaded base bone-rest pose. We reuse it for
   *  remote characters so the static pose matches the local player's. */
  basePositions: readonly [number, number, number][];
}

const NOD_BPM = 87;
const NOD_AMPLITUDE = 0.075;
const NOD_DOWN_FRAC = 0.35;
const NOD_START_DELAY_SEC = 0.5;

const DROP_IN_DURATION = 0.32;        // seconds, y motion
const DROP_IN_BOUNCE_DURATION = 0.20; // seconds, scale rebound
const POOF_DURATION = 0.12;           // seconds

interface RemoteInstance {
  sessionId: string;
  view: CharacterView;
  shadow: THREE.Mesh;
  worldX: number;
  /** 1..3 — local-side slot, NOT server's joinIndex. Local player is
   *  always at slot 0 (world x=0); non-local members fill 1/2/3 in
   *  their arrival order from this client's perspective. Vacated
   *  slots are re-filled by future arrivals (lowest unused). */
  slot: number;
  /** Eased mouse-look angles. Each character looks at the user's local
   *  mouse; the per-character anchor adjusts so off-axis characters tilt
   *  appropriately. */
  headYaw: number;
  headPitch: number;
  /** Snapshots used to drive the bone view. */
  prevSnap: CharacterSnapshot;
  currSnap: CharacterSnapshot;
  /** 0..1 progress through the drop-in animation. 1 = fully landed. */
  dropInProgress: number;
  /** Set on the way out; ticks 0..1, then the instance is removed. */
  poofProgress: number | null;
  /** DOM head badge (name pin). */
  badge: HTMLElement;
  /** Whether to paint the owner crown overlay above the badge. */
  isOwner: boolean;
}

export class MultiCharacterStage {
  private instances = new Map<string, RemoteInstance>();
  private mouseX = innerWidth / 2;
  private mouseY = innerHeight / 2;
  private styleEl: HTMLStyleElement | null = null;
  /** True after dispose() — guards against late timer callbacks. */
  private disposed = false;

  constructor(private readonly handle: StageHandle) {
    this.installStyles();
    addEventListener('mousemove', this.onMouseMove);
    addEventListener('pointermove', this.onMouseMove);
  }

  /** Diff against the current set of remote characters. Members of the
   *  party that are local OR already present are skipped; new members are
   *  spawned with a drop-in animation; departed members are poofed out. */
  setMembers(members: ReadonlyArray<PartyMemberView>): void {
    if (this.disposed) return;
    const remoteMembers = members.filter((m) => !m.isLocal);
    const incoming = new Set(remoteMembers.map((m) => m.sessionId));

    // Departures.
    for (const [sessionId, inst] of this.instances) {
      if (!incoming.has(sessionId) && inst.poofProgress === null) {
        inst.poofProgress = 0;
      }
    }

    // Arrivals + updates.
    for (const m of remoteMembers) {
      const existing = this.instances.get(m.sessionId);
      if (existing) {
        // Update palette / hat / owner crown / level on the live instance.
        existing.view.setPalette(paletteFromBase(m.color));
        existing.view.setHat(m.hatId ?? null);
        const crownChanged = existing.isOwner !== m.isOwner;
        existing.isOwner = m.isOwner;
        if (crownChanged) this.refreshBadgeMarkup(existing, m);
        // Refresh badge text on rename.
        const labelEl = existing.badge.querySelector('.label-text') as HTMLElement | null;
        if (labelEl) labelEl.textContent = m.name;
      } else {
        this.spawn(m);
      }
    }
  }

  /** Per-frame update. Called from HomeScreen's rAF loop after the local
   *  character's pose has been computed. */
  tick(nowMs: number, dt: number): void {
    if (this.disposed) return;
    const beatPhase = this.computeNodPhase(nowMs);
    const nodPitch = NOD_AMPLITUDE * this.envelopeFromPhase(beatPhase);

    for (const [sessionId, inst] of Array.from(this.instances.entries())) {
      // Drop-in animation.
      if (inst.dropInProgress < 1) {
        inst.dropInProgress = Math.min(1, inst.dropInProgress + dt / (DROP_IN_DURATION + DROP_IN_BOUNCE_DURATION));
      }
      // Poof animation — we run scale up + opacity down in parallel; on
      // completion the instance is destroyed.
      if (inst.poofProgress !== null) {
        inst.poofProgress = Math.min(1, inst.poofProgress + dt / POOF_DURATION);
        const t = inst.poofProgress;
        const s = 1 + 0.4 * t;
        inst.view.group.scale.set(s, s, s);
        inst.view.group.position.y = 0;
        const mat = inst.shadow.material;
        if (!Array.isArray(mat)) mat.opacity = 1 - t;
        inst.badge.style.opacity = String(1 - t);
        if (inst.poofProgress >= 1) {
          this.dispose1(sessionId);
        }
        // Even while poofing, we still apply pose so the squish reads as
        // the character motion rather than a static image.
      }

      // Head tracking — computed once per character against its own
      // anchor screen position.
      this.stepHeadLook(inst, dt);

      // Apply pose to the bone snapshots, then push to the view.
      this.applyPose(inst, nodPitch);
      inst.view.update(inst.prevSnap, inst.currSnap, 1, dt);

      // Drop-in y motion + squish bounce.
      const dropY = this.dropInY(inst.dropInProgress);
      const dropScale = this.dropInScale(inst.dropInProgress);
      inst.view.group.position.set(inst.worldX, dropY, 0);
      inst.shadow.position.set(inst.worldX, 0.02, 0.3);
      // Squish bounce only applies during the post-impact rebound; if
      // we're poofing, the poof scale wins.
      if (inst.poofProgress === null) {
        inst.view.group.scale.set(dropScale.x, dropScale.y, dropScale.z);
      }

      // Update DOM badge position.
      this.positionBadge(inst);
    }
  }

  /** Tear down all remote characters AND remove the global mouse listener.
   *  Called when leaving online play / dispose home screen. */
  dispose(): void {
    this.disposed = true;
    removeEventListener('mousemove', this.onMouseMove);
    removeEventListener('pointermove', this.onMouseMove);
    for (const sessionId of Array.from(this.instances.keys())) {
      this.dispose1(sessionId);
    }
    this.styleEl?.remove();
    this.styleEl = null;
  }

  // -- internals ----------------------------------------------------------

  private spawn(m: PartyMemberView): void {
    const slot = this.allocSlot();
    const view = new CharacterView(paletteFromBase(m.color), { hatId: m.hatId });
    const worldX = MultiCharacterStage.worldXForSlot(slot);
    view.group.position.set(worldX, 6, 0); // start above frame
    this.handle.scene.add(view.group);

    const shadow = makeGroundShadow();
    shadow.position.set(worldX, 0.02, 0.3);
    this.handle.scene.add(shadow);

    const inst: RemoteInstance = {
      sessionId: m.sessionId,
      view,
      shadow,
      worldX,
      slot,
      headYaw: 0,
      headPitch: 0,
      prevSnap: emptySnapshot(0, BONE_COUNT),
      currSnap: emptySnapshot(0, BONE_COUNT),
      dropInProgress: 0,
      poofProgress: null,
      badge: document.createElement('div'),
      isOwner: m.isOwner
    };
    inst.badge.className = 'party-head-badge';
    this.refreshBadgeMarkup(inst, m);
    this.handle.badgeRoot.appendChild(inst.badge);

    this.instances.set(m.sessionId, inst);
  }

  /** Pick the lowest unused slot in 1..3 from the perspective of this
   *  client. Local player owns slot 0 implicitly; non-locals fill 1/2/3
   *  in their arrival order. Vacated slots can be re-filled by future
   *  arrivals (so a 3-person party that loses one and gains another
   *  ends with the new person in the middle slot, not slot 4). */
  private allocSlot(): number {
    const used = new Set<number>();
    for (const inst of this.instances.values()) used.add(inst.slot);
    for (let i = 1; i <= 3; i++) if (!used.has(i)) return i;
    return 3;
  }

  private dispose1(sessionId: string): void {
    const inst = this.instances.get(sessionId);
    if (!inst) return;
    this.handle.scene.remove(inst.view.group);
    this.handle.scene.remove(inst.shadow);
    inst.shadow.geometry.dispose();
    if (inst.shadow.material instanceof THREE.Material) inst.shadow.material.dispose();
    inst.badge.remove();
    this.instances.delete(sessionId);
  }

  private static worldXForSlot(slot: number): number {
    switch (slot) {
      case 1: return +1.2;
      case 2: return -1.2;
      case 3: return +2.4;
      default: return 0;
    }
  }

  private onMouseMove = (e: MouseEvent | PointerEvent): void => {
    this.mouseX = e.clientX;
    this.mouseY = e.clientY;
  };

  private stepHeadLook(inst: RemoteInstance, dt: number): void {
    // Project the character's head world position to canvas coords so we
    // can compute mouse-deltas anchored to the actual on-screen head.
    const canvasSize = this.handle.getCanvasSize();
    const headWorld = new THREE.Vector3(inst.worldX, 1.7, 0);
    const ndc = headWorld.clone().project(this.handle.camera);
    const canvasRect = this.handle.badgeRoot.getBoundingClientRect();
    const headScreenX = canvasRect.left + (ndc.x * 0.5 + 0.5) * canvasSize.width;
    const headScreenY = canvasRect.top + (1 - (ndc.y * 0.5 + 0.5)) * canvasSize.height;

    const dx = this.mouseX - headScreenX;
    const dy = this.mouseY - headScreenY;
    const focal = Math.max(1, innerWidth * 0.55);
    const YAW_MAX = 0.95;
    const PITCH_MAX = 0.55;
    const yawT = Math.max(-YAW_MAX, Math.min(YAW_MAX, Math.atan2(dx, focal)));
    const pitchT = Math.max(-PITCH_MAX, Math.min(PITCH_MAX, Math.atan2(dy, focal)));
    const k = Math.min(1, dt * 10);
    inst.headYaw += (yawT - inst.headYaw) * k;
    inst.headPitch += (pitchT - inst.headPitch) * k;
  }

  private applyPose(inst: RemoteInstance, nodPitch: number): void {
    const yawBody = 0; // remote characters face camera; no user drag
    const sinB = Math.sin(yawBody), cosB = Math.cos(yawBody);
    const qBody = quatFromAxisAngle({ x: 0, y: 1, z: 0 }, yawBody);
    const qYawHead = quatFromAxisAngle({ x: 0, y: 1, z: 0 }, inst.headYaw);
    const qPitchHead = quatFromAxisAngle({ x: 1, y: 0, z: 0 }, inst.headPitch + nodPitch);
    const qHead = quatMul(qYawHead, qPitchHead);

    for (let i = 0; i < BONE_COUNT; i++) {
      const o = i * 7;
      const p = this.handle.basePositions[i]!;
      const rx = cosB * p[0] + sinB * p[2];
      const rz = -sinB * p[0] + cosB * p[2];
      const q = i === Bone.Head ? qHead : qBody;
      inst.currSnap.bones[o + 0] = rx;
      inst.currSnap.bones[o + 1] = p[1];
      inst.currSnap.bones[o + 2] = rz;
      inst.currSnap.bones[o + 3] = q.x;
      inst.currSnap.bones[o + 4] = q.y;
      inst.currSnap.bones[o + 5] = q.z;
      inst.currSnap.bones[o + 6] = q.w;
    }
    inst.prevSnap.bones.set(inst.currSnap.bones);
    inst.currSnap.facingYaw = yawBody;
    inst.currSnap.pelvisY = this.handle.basePositions[Bone.Pelvis]![1];
    inst.prevSnap.facingYaw = yawBody;
    inst.prevSnap.pelvisY = inst.currSnap.pelvisY;
  }

  /** Project the head world position to screen and place the DOM badge
   *  centred slightly above the hat. */
  private positionBadge(inst: RemoteInstance): void {
    const canvasSize = this.handle.getCanvasSize();
    const headTopWorld = new THREE.Vector3(inst.worldX, 2.05, 0);
    const ndc = headTopWorld.clone().project(this.handle.camera);
    const x = (ndc.x * 0.5 + 0.5) * canvasSize.width;
    const y = (1 - (ndc.y * 0.5 + 0.5)) * canvasSize.height;
    inst.badge.style.transform = `translate(calc(${x}px - 50%), calc(${y}px - 100%))`;
  }

  private refreshBadgeMarkup(inst: RemoteInstance, m: PartyMemberView): void {
    const colors = badgeColorsFromBase(m.color);
    inst.badge.innerHTML = `
      <div class="party-head-svg">${renderHeadLabelSvg({ text: m.name, ...colors })}</div>
      <div class="label-text" style="display:none;">${escapeHtml(m.name)}</div>
    `;
    void m.level;
    void inst.isOwner;
  }

  private computeNodPhase(nowMs: number): number {
    const bps = NOD_BPM / 60;
    const t = nowMs / 1000 - NOD_START_DELAY_SEC;
    return ((t * bps) % 1 + 1) % 1;
  }

  private envelopeFromPhase(phase: number): number {
    const dF = NOD_DOWN_FRAC;
    if (phase < dF) {
      const x = phase / dF;
      return 0.5 - 0.5 * Math.cos(x * Math.PI);
    } else {
      const x = (phase - dF) / (1 - dF);
      return 0.5 + 0.5 * Math.cos(x * Math.PI);
    }
  }

  private dropInY(progress: number): number {
    if (progress <= 0) return 6;
    const yPhase = Math.min(1, progress / (DROP_IN_DURATION / (DROP_IN_DURATION + DROP_IN_BOUNCE_DURATION)));
    if (yPhase < 1) {
      // easeOutCubic from y=6 to y=0
      const eased = 1 - Math.pow(1 - yPhase, 3);
      return 6 * (1 - eased);
    }
    return 0;
  }

  private dropInScale(progress: number): { x: number; y: number; z: number } {
    if (progress <= 0) return { x: 1, y: 1, z: 1 };
    const yPhase = Math.min(1, progress / (DROP_IN_DURATION / (DROP_IN_DURATION + DROP_IN_BOUNCE_DURATION)));
    if (yPhase < 1) return { x: 1, y: 1, z: 1 };
    // Bounce: scale.y dips to 0.7, scale.x/z to 1.2, then easeOutBack
    // back to 1.0.
    const bouncePhase = (progress - DROP_IN_DURATION / (DROP_IN_DURATION + DROP_IN_BOUNCE_DURATION))
      / (DROP_IN_BOUNCE_DURATION / (DROP_IN_DURATION + DROP_IN_BOUNCE_DURATION));
    const t = Math.max(0, Math.min(1, bouncePhase));
    // Custom curve: starts at the squashed extremum, eases back to 1.
    const ease = easeOutBack(t);
    const yMin = 0.7;
    const xMax = 1.2;
    const sy = yMin + (1 - yMin) * ease;
    const sx = xMax + (1 - xMax) * ease;
    return { x: sx, y: sy, z: sx };
  }

  private installStyles(): void {
    if (document.getElementById('multi-char-stage-styles')) return;
    this.styleEl = document.createElement('style');
    this.styleEl.id = 'multi-char-stage-styles';
    this.styleEl.textContent = `
      .party-head-badge {
        position: absolute; left: 0; top: 0;
        width: 108px; height: 93px;
        pointer-events: none;
        will-change: transform;
        transition: opacity 120ms ease;
      }
      .party-head-badge .party-head-svg svg {
        width: 108px; height: 93px;
        filter: drop-shadow(0 4px 6px rgba(0,0,0,0.28));
      }
      /* Hide the head pins entirely while in online-active mode (lobby +
         team-select). Names already appear on the panel + team rows; the
         floating pins above each character were redundant. They DO show
         on the home + changing-room screens (not online-active). */
      #home-screen.online-active .party-head-badge { display: none; }
    `;
    document.head.appendChild(this.styleEl);
  }
}

function makeGroundShadow(): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 256;
  const ctx = canvas.getContext('2d')!;
  const grad = ctx.createRadialGradient(128, 128, 0, 128, 128, 128);
  grad.addColorStop(0.00, 'rgba(11, 42, 56, 0.85)');
  grad.addColorStop(0.30, 'rgba(11, 42, 56, 0.55)');
  grad.addColorStop(0.60, 'rgba(11, 42, 56, 0.18)');
  grad.addColorStop(1.00, 'rgba(11, 42, 56, 0.00)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, 256, 256);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  const plane = new THREE.Mesh(
    new THREE.PlaneGeometry(1.5, 1.5),
    new THREE.MeshBasicMaterial({ map: tex, transparent: true, depthWrite: false, opacity: 1 })
  );
  plane.rotation.x = -Math.PI / 2;
  return plane;
}

function easeOutBack(t: number): number {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
